import { type JSX, useEffect, useMemo, useState } from 'react';
import {
  addToast,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
} from '@heroui/react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  GitBranch,
  Link2,
  Plus,
} from 'lucide-react';

import {
  useAddProductRepository,
  useCreateProduct,
  useProducts,
  useRemoveProductRepository,
} from '~/hooks/useProducts';
import { useRepositories } from '~/hooks/useRepositories';

import { Input, Textarea } from '~/components/Field';
import { FilterBar } from '~/components/layout/FilterBar';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Modal } from '~/components/Modal';

import type { Product, Repository } from '~/types';

type SortDirection = 'asc' | 'desc';

/** Sentinel key for the "All owners" filter option — HeroUI Select keys can't be empty strings. */
const ALL_OWNERS = '__all__';

interface RepositoryRowProps {
  isJustLinked: boolean;
  isPending: boolean;
  onProductsChange: (repository: Repository, productIds: string[]) => void;
  onRepositoryLinked: (repositoryId: string) => void;
  products: Product[];
  repository: Repository;
}

export interface RepositoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Scopes the table to repos with no product yet — same view, pre-filtered. */
  unlinkedOnly?: boolean;
}

/**
 * The Products cell: a static list of the repo's current products, plus a
 * link-icon button that opens a popover for changing the set — either by
 * checking existing products, or by creating a brand new one inline (name +
 * description) that's linked to this repo as soon as it's created. Kept
 * static-by-default (rather than an always-open control) so the table reads
 * as a repository inventory first, with linking as a secondary action.
 *
 * The popover sizes to its content (`w-max`) instead of a fixed width, so a
 * long repo or product name is never clipped.
 */
function RepositoryProductsCell({
  isJustLinked,
  isPending,
  onProductsChange,
  onRepositoryLinked,
  products,
  repository,
}: RepositoryRowProps): JSX.Element {
  const createProduct = useCreateProduct();
  const addProductRepository = useAddProductRepository();

  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const linkedProductIds = repository.productIds ?? [];
  const linkedProducts = products.filter(product => linkedProductIds.includes(product.id));
  const isCreatePending = createProduct.isPending || addProductRepository.isPending;

  const resetCreateForm = (): void => {
    setIsCreating(false);
    setNewName('');
    setNewDescription('');
  };

  const handleCreateAndLink = (): void => {
    const name = newName.trim();
    if (!name) return;

    createProduct.mutate(
      { description: newDescription.trim() || undefined, name },
      {
        onError: () => {
          addToast({
            color: 'danger',
            description: `Failed to create "${name}". Please try again.`,
          });
        },
        onSuccess: product => {
          addProductRepository.mutate(
            { productId: product.id, repositoryId: repository.id },
            {
              onError: () => {
                addToast({
                  color: 'danger',
                  description: `"${product.name}" was created but failed to link to ${repository.fullName}. Please try again.`,
                });
              },
              onSuccess: () => {
                resetCreateForm();
                onRepositoryLinked(repository.id);
              },
            },
          );
        },
      },
    );
  };

  const linkedProductNames = linkedProducts.map(product => product.name).join(', ');

  return (
    <div className="flex w-56 shrink-0 items-center gap-2">
      <div
        className={`min-w-0 flex-1 truncate text-xs ${
          isJustLinked ? 'text-primary font-medium' : 'text-muted'
        }`}
        title={linkedProductNames || undefined}
      >
        {linkedProducts.length > 0 ? (
          linkedProductNames
        ) : (
          <span className="italic text-muted/60">Not linked</span>
        )}
      </div>
      <Popover placement="bottom-end" onOpenChange={open => !open && resetCreateForm()}>
        <PopoverTrigger>
          <Button
            isIconOnly
            aria-label={`Link ${repository.fullName} to products`}
            isDisabled={isPending}
            size="sm"
            variant="light"
          >
            <Link2 aria-hidden="true" className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="w-max min-w-64 p-2" data-testid={`repo-link-popover-${repository.id}`}>
            {isCreating ? (
              <form
                className="flex flex-col gap-2"
                onSubmit={e => {
                  e.preventDefault();
                  handleCreateAndLink();
                }}
              >
                <p className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-muted">
                  New product
                </p>
                <Input
                  aria-label="New product name"
                  isDisabled={isCreatePending}
                  placeholder="Product name"
                  value={newName}
                  onValueChange={setNewName}
                />
                <Textarea
                  aria-label="New product description"
                  isDisabled={isCreatePending}
                  minRows={2}
                  placeholder="Description (optional)"
                  value={newDescription}
                  onValueChange={setNewDescription}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    isDisabled={isCreatePending}
                    type="button"
                    variant="light"
                    onPress={resetCreateForm}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    isDisabled={isCreatePending || !newName.trim()}
                    type="submit"
                  >
                    {isCreatePending ? <LoadingSpinner /> : 'Create & link'}
                  </Button>
                </div>
              </form>
            ) : (
              <>
                <p className="mb-2 whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-muted">
                  Link to products
                </p>
                {products.length > 0 && (
                  <>
                    <CheckboxGroup
                      aria-label={`Products linked to ${repository.fullName}`}
                      value={linkedProductIds}
                      onValueChange={ids => onProductsChange(repository, ids)}
                    >
                      {products.map(product => (
                        <Checkbox
                          key={product.id}
                          aria-label={product.name}
                          className="whitespace-nowrap"
                          value={product.id}
                        >
                          {product.name}
                        </Checkbox>
                      ))}
                    </CheckboxGroup>
                    <Divider className="my-2" />
                  </>
                )}
                <Button
                  className="w-full justify-start"
                  size="sm"
                  startContent={<Plus aria-hidden="true" className="h-3.5 w-3.5" />}
                  variant="light"
                  onPress={() => setIsCreating(true)}
                >
                  New product
                </Button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function RepositoryRow(props: RepositoryRowProps): JSX.Element {
  const { isJustLinked, repository } = props;

  return (
    <div
      className="flex items-start gap-3 border-b border-default px-3 py-3 last:border-b-0"
      data-testid={`repo-row-${repository.id}`}
    >
      {isJustLinked ? (
        <CheckCircle2
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 text-success"
          data-testid="repo-linked-indicator"
        />
      ) : (
        <GitBranch aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
      )}
      <div className="w-72 shrink-0">
        <p className="text-xs text-muted">{repository.owner}</p>
        <p className="text-sm font-medium text-foreground">{repository.name}</p>
      </div>
      <div className="hidden min-w-0 flex-1 md:block">
        {repository.description && (
          <p className="break-words text-xs text-muted">{repository.description}</p>
        )}
      </div>
      <RepositoryProductsCell {...props} />
    </div>
  );
}

/**
 * Column headers for the repository table — hidden below `md` since the row
 * layout collapses to name-only at that breakpoint (see RepositoryRow). The
 * Products header doubles as a sort toggle: unclicked, the list stays in its
 * natural load order (so linking a repo mid-session can't make its row jump);
 * the first click sorts ascending (unlinked repos first, since an empty sort
 * key sorts before any product name), the next flips to descending (unlinked
 * last).
 */
function RepositoryTableHeader({
  onToggleSort,
  sortDirection,
}: {
  onToggleSort: () => void;
  sortDirection: SortDirection | null;
}): JSX.Element {
  const SortIcon =
    sortDirection === 'asc' ? ArrowUp : sortDirection === 'desc' ? ArrowDown : ArrowUpDown;

  return (
    <div className="hidden items-center gap-3 border-b border-default bg-surface-elevated px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted md:flex">
      <div className="w-4 shrink-0" />
      <div className="w-72 shrink-0">Repository</div>
      <div className="min-w-0 flex-1">Description</div>
      <button
        className="flex w-56 shrink-0 items-center gap-1 hover:text-foreground"
        type="button"
        onClick={onToggleSort}
      >
        Products
        <SortIcon aria-hidden="true" className="h-3 w-3" />
      </button>
    </div>
  );
}

/**
 * Inventory of every repository known to the organization — a repository
 * viewer first; linking a repo to one or more products is one action
 * available from it, via each row's link-icon popover (not the main point
 * of the view, so it stays out of the way until clicked).
 *
 * The list stays in its natural load order until the user clicks the
 * Products column header — linking a repo mid-session must not make its row
 * jump, or there's no way to tell the link actually happened. Once clicked,
 * the header sorts by linked product name(s), unlinked repos grouped at one
 * end (ascending: first, descending: last) — no separate "unlinked only"
 * filter needed.
 *
 * Pattern: docs/design-patterns/frontend/anti-patterns/request-fan-out.md —
 * linked/unlinked state is derived from the single useRepositories() list,
 * never fetched per-row.
 */
export function RepositoriesModal({
  isOpen,
  onClose,
  unlinkedOnly = false,
}: RepositoriesModalProps): JSX.Element {
  const { data: allRepositories, isError, isLoading } = useRepositories();

  // Snapshot of which repo IDs were unlinked when this session opened. Linking
  // a repo mid-session must not make its row vanish — it should keep showing
  // (with its newly-linked product) until the modal is closed and reopened,
  // at which point the snapshot is dropped and recomputed from live data.
  const [unlinkedSnapshot, setUnlinkedSnapshot] = useState<Set<string> | null>(null);
  // Repo IDs linked to a product during this modal session — drives the
  // "just linked" checkmark and highlighted product name. Session-scoped
  // like unlinkedSnapshot above: reset together whenever the modal closes.
  const [justLinkedRepoIds, setJustLinkedRepoIds] = useState<Set<string>>(new Set());
  const markJustLinked = (repositoryId: string): void => {
    setJustLinkedRepoIds(prev => new Set(prev).add(repositoryId));
  };

  useEffect(() => {
    if (!isOpen) {
      setUnlinkedSnapshot(null);
      setJustLinkedRepoIds(new Set());
      return;
    }
    if (unlinkedOnly && allRepositories) {
      setUnlinkedSnapshot(
        prev =>
          prev ??
          new Set(
            allRepositories
              .filter(repository => (repository.productIds?.length ?? 0) === 0)
              .map(repository => repository.id),
          ),
      );
    }
  }, [isOpen, unlinkedOnly, allRepositories]);

  const repositories = useMemo(() => {
    if (!unlinkedOnly) return allRepositories;
    if (!unlinkedSnapshot) {
      return allRepositories?.filter(repository => (repository.productIds?.length ?? 0) === 0);
    }
    return allRepositories?.filter(repository => unlinkedSnapshot.has(repository.id));
  }, [allRepositories, unlinkedOnly, unlinkedSnapshot]);

  const { data: products } = useProducts();
  const addProductRepository = useAddProductRepository();
  const removeProductRepository = useRemoveProductRepository();

  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  // null = natural load order (default); set only once the user clicks the
  // Products header, so linking a repo mid-session can't silently reorder it.
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);
  // Race guard: tracks repositoryIds with an in-flight mutation so only that
  // row's popover disables — unrelated rows stay interactive.
  const [pendingRepoIds, setPendingRepoIds] = useState<Set<string>>(new Set());

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    products?.forEach(product => map.set(product.id, product.name));
    return map;
  }, [products]);

  const owners = useMemo(
    () =>
      [...new Set((repositories ?? []).map(repository => repository.owner))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [repositories],
  );

  const sortedRepositories = useMemo(() => {
    const list = repositories ?? [];
    if (!sortDirection) return list;

    const sortKeyFor = (repository: Repository): string =>
      (repository.productIds ?? [])
        .map(id => productNameById.get(id))
        .filter((name): name is string => !!name)
        .sort((a, b) => a.localeCompare(b))
        .join(', ');

    return [...list].sort((a, b) => {
      const comparison = sortKeyFor(a).localeCompare(sortKeyFor(b)) || a.name.localeCompare(b.name);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [repositories, productNameById, sortDirection]);

  const filteredRepositories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return sortedRepositories.filter(repository => {
      const matchesOwner = !ownerFilter || repository.owner === ownerFilter;
      const matchesQuery =
        !query ||
        repository.name.toLowerCase().includes(query) ||
        repository.fullName.toLowerCase().includes(query);
      return matchesOwner && matchesQuery;
    });
  }, [sortedRepositories, searchQuery, ownerFilter]);

  const repositoryCount = repositories?.length ?? 0;
  const shownCount = filteredRepositories.length;

  const setRepoPending = (repositoryId: string, isPending: boolean): void => {
    setPendingRepoIds(prev => {
      const next = new Set(prev);
      if (isPending) {
        next.add(repositoryId);
      } else {
        next.delete(repositoryId);
      }
      return next;
    });
  };

  const handleProductsChange = (repository: Repository, nextProductIds: string[]): void => {
    const previousProductIds = repository.productIds ?? [];
    const added = nextProductIds.filter(id => !previousProductIds.includes(id));
    const removed = previousProductIds.filter(id => !nextProductIds.includes(id));
    if (added.length === 0 && removed.length === 0) return;

    setRepoPending(repository.id, true);
    const mutations = [
      ...added.map(productId => ({ mutation: addProductRepository, productId, shouldLink: true })),
      ...removed.map(productId => ({
        mutation: removeProductRepository,
        productId,
        shouldLink: false,
      })),
    ];

    Promise.allSettled(
      mutations.map(
        ({ mutation, productId, shouldLink }) =>
          new Promise<void>((resolve, reject) => {
            mutation.mutate(
              { productId, repositoryId: repository.id },
              {
                onError: () => {
                  addToast({
                    color: 'danger',
                    description: `Failed to ${shouldLink ? 'link' : 'unlink'} ${repository.fullName}. Please try again.`,
                  });
                  reject(new Error('mutation failed'));
                },
                onSuccess: () => resolve(),
              },
            );
          }),
      ),
    )
      .then(results => {
        const hasSuccessfulLink = results.some(
          (result, index) => result.status === 'fulfilled' && mutations[index].shouldLink,
        );
        if (hasSuccessfulLink) {
          markJustLinked(repository.id);
        }
      })
      .catch(() => undefined)
      .finally(() => setRepoPending(repository.id, false));
  };

  return (
    <Modal isOpen={isOpen} maxWidth="4xl" placement="top" onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-0.5">
          <span>Repositories</span>
          <span className="text-sm font-normal text-muted">
            {unlinkedOnly
              ? `${repositoryCount} repositor${repositoryCount !== 1 ? 'ies' : 'y'} not yet linked to a product.`
              : `${repositoryCount} repositor${repositoryCount !== 1 ? 'ies' : 'y'} in your organization.`}
          </span>
        </ModalHeader>
        <Divider />
        <ModalBody className="flex max-h-[70vh] flex-col gap-3 overflow-hidden">
          {isLoading && <p className="text-sm text-muted">Loading repositories...</p>}
          {isError && (
            <p className="text-sm text-error">
              Failed to load repositories. Please try again later.
            </p>
          )}
          {!isLoading && !isError && repositoryCount === 0 && (
            <p className="text-sm text-muted">No repositories found in this organization.</p>
          )}
          {!isLoading && !isError && repositoryCount > 0 && (
            <>
              <FilterBar
                controls={
                  <Select
                    aria-label="Filter by owner"
                    className="w-40"
                    disallowEmptySelection
                    selectedKeys={[ownerFilter || ALL_OWNERS]}
                    size="sm"
                    onSelectionChange={keys =>
                      setOwnerFilter(
                        ([...keys][0] as string) === ALL_OWNERS ? '' : ([...keys][0] as string),
                      )
                    }
                  >
                    {[
                      <SelectItem key={ALL_OWNERS}>All owners</SelectItem>,
                      ...owners.map(owner => <SelectItem key={owner}>{owner}</SelectItem>),
                    ]}
                  </Select>
                }
                count={{ shown: shownCount, total: repositoryCount }}
                searchPlaceholder="Search repositories..."
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
              />
              {shownCount === 0 ? (
                <p className="text-sm text-muted">No repositories match your search.</p>
              ) : (
                <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-default">
                  <RepositoryTableHeader
                    sortDirection={sortDirection}
                    onToggleSort={() => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                  />
                  {filteredRepositories.map(repository => (
                    <RepositoryRow
                      key={repository.id}
                      isJustLinked={
                        justLinkedRepoIds.has(repository.id) &&
                        (repository.productIds?.length ?? 0) > 0
                      }
                      isPending={pendingRepoIds.has(repository.id)}
                      products={products ?? []}
                      repository={repository}
                      onProductsChange={handleProductsChange}
                      onRepositoryLinked={markJustLinked}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </ModalBody>
        <Divider />
        <ModalFooter>
          <Button data-testid="repositories-modal-close" variant="light" onPress={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
